import { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import cloneDeep from "lodash.clonedeep";

import AuthApi from "api/auth";
import useNotifier from "hooks/useNotifier";
import useBoundStore from "store";
import { SECRET_SANTA_TAB, SecretSantaTabKey, MONEY_MANAGER_TAB, MoneyManagerTabKey } from "store/selectedSlice";

interface UrlHandlerProps {
  children: React.ReactNode;
}

export const eventUrlRegex = /^\/group\/[0-9]*\/event\/[0-9]*$/;
export const secretSantaUrlRegex = /^\/group\/[0-9]*\/event\/[0-9]*\/apps\/secret-santa$/;
export const moneyManagerUrlRegex = /^\/group\/[0-9]*\/event\/[0-9]*\/apps\/money-manager$/;

function UrlHandler(props: UrlHandlerProps) {
  const { children } = props;
  const redirect = useBoundStore((state) => state.navigation.redirect);
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const setSelected = useBoundStore((state) => state.selected.set);
  const setRedirect = useBoundStore((state) => state.navigation.setRedirect);
  const setDesktopHistory = useBoundStore((state) => state.navigation.setDesktopHistory);
  const { showNotification } = useNotifier();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Set redirect url
   */
  useEffect(() => {
    if (!redirect.location) {
      setRedirect({ location: cloneDeep(location) });
    }
  }, [redirect.location, setRedirect]);

  useEffect(() => {});

  /**
   * Redirect to only specific urls (Mobile devices only) The same type of redirect
   * can be found in the DesktopPrivateRoutes
   */
  useEffect(() => {
    if (redirect.location && !redirect.triggered) {
      setRedirect({ triggered: true });
      const redirectLocation = redirect.location;
      const locationPathname = redirectLocation.pathname;
      const hashParams = new URLSearchParams(redirectLocation.hash.slice(1));
      const message = hashParams.get("message");
      const urlType = hashParams.get("type");

      const accessToken = hashParams.get("access_token");
      if (accessToken) {
        AuthApi.setUserSession(hashParams);
      }

      if (message) {
        showNotification({
          severity: "warning",
          message: message.replaceAll("+", " "),
        });
      }

      if (urlType === "email_change") {
        showNotification({
          message: "Email successfully changed",
          severity: "success",
        });
      }

      if (urlType === "recovery") {
        navigate("/confirm-reset-password", { replace: true });
        return;
      }

      if (eventUrlRegex.test(locationPathname)) {
        const params = locationPathname.split("/");
        const groupId = parseInt(params[2]);
        const eventId = parseInt(params[4]);

        setSelected({ groupId, eventId });
        if (isMobileDevice) {
          navigate(`/group/${groupId}`);
          navigate(`/group/${groupId}/event/${eventId}`);
        } else {
          setSelected({ groupId, eventId });
          setDesktopHistory({ goTo: { GRID2: "group", GRID3: "event" } })();
          navigate("/", { replace: true });
        }

        return;
      }

      if (secretSantaUrlRegex.test(locationPathname)) {
        const params = locationPathname.split("/");
        const groupId = parseInt(params[2]);
        const eventId = parseInt(params[4]);
        const tab = hashParams.get("tab");
        const secretSantaTab: SecretSantaTabKey =
          tab && tab in SECRET_SANTA_TAB ? (tab as SecretSantaTabKey) : "my-pair";

        setSelected({
          groupId,
          eventId,
          secretSantaTab: SECRET_SANTA_TAB[secretSantaTab],
        });

        if (isMobileDevice) {
          navigate(`/group/${groupId}`);
          navigate(`/group/${groupId}/event/${eventId}`);
          navigate(`/group/${groupId}/event/${eventId}/apps/secret-santa`);
        } else {
          setDesktopHistory({
            goTo: { GRID2: "group", GRID3: ["event", "secret-santa"] },
          })();
          navigate("/", { replace: true });
        }

        return;
      }

      if (moneyManagerUrlRegex.test(locationPathname)) {
        const params = locationPathname.split("/");
        const groupId = parseInt(params[2]);
        const eventId = parseInt(params[4]);
        const tab = hashParams.get("tab");
        const moneyManagerTab: MoneyManagerTabKey =
          tab && tab in MONEY_MANAGER_TAB ? (tab as MoneyManagerTabKey) : "setup";

        setSelected({
          groupId,
          eventId,
          moneyManagerTab: MONEY_MANAGER_TAB[moneyManagerTab],
        });

        if (isMobileDevice) {
          navigate(`/group/${groupId}`);
          navigate(`/group/${groupId}/event/${eventId}`);
          navigate(`/group/${groupId}/event/${eventId}/apps/money-manager`);
        } else {
          setDesktopHistory({
            goTo: { GRID2: "group", GRID3: ["event", "money-manager"] },
          })();
          navigate("/", { replace: true });
        }

        return;
      }

      if (locationPathname === "/friends/add-friend") {
        if (isMobileDevice) {
          navigate("/friends");
          navigate(`/friends/add-friend${redirectLocation.search}`);
        } else {
          // this is needed to get name and hash from the url inside the add friend page
          navigate(`/friends/add-friend${redirectLocation.search}`);
          setDesktopHistory({ goTo: { GRID1: ["friends", "add-friends"] } })();
        }
        return;
      }

      if (locationPathname === "/join-via-link") {
        const inviteLink = hashParams.get("slug");
        setSelected({ groupInviteLink: inviteLink });
      }
    }
  }, [isMobileDevice, location, redirect, setRedirect, showNotification]);

  if (!redirect.location) {
    return <Navigate to='/' replace />;
  }

  return children;
}

export default UrlHandler;
